// Phase 2: Guide pages
import type { Guide } from "./types";
import { REVIEW_COUNT_TEXT } from "@/lib/booking";

export const guides: Guide[] = [
  {
    slug: "vad-ska-man-saga-till-barberaren-om-man-vill-ha-en-fade",
    title: "Vad ska man säga till barberaren om man vill ha en fade?",
    category: "Fade-guider",
    excerpt: "Vet inte vad du ska säga till barberaren? Här är orden du behöver för att kommunicera exakt vilken fade du vill ha.",
    content: "Att kommunicera med sin barberare kan kännas svårt om du inte kan de rätta termerna. Men du behöver inte ett perfekt fackspråk – några nyckelord räcker. Den här guiden ger dig de fraser du behöver för att komma hem med exakt den fade du ville ha.",
    relatedServices: ["taper-fade", "herrklippning"],
    relatedSalons: ["ahlens-city", "taby", "akersberga", "arninge"],
    relatedGuides: ["herrfrisyrer-2026", "barberare-eller-frisor-vad-ar-bast-for-man-i-stockholm"],
    faq: [
      { q: "Måste man veta vad en fade heter?", a: "Nej, en bild räcker. Men termerna low/mid/high och skin/taper hjälper barberaren att förstå snabbare." },
      { q: "Hur säger man att man vill ha skarpa linjer?", a: "Säg 'jag vill ha tydliga linjer' eller 'line-up' runt öronen, nacken och hårfästet." },
      { q: "Vad betyder hard part?", a: "En inrakat linje i håret som delar frisyren – populärt ihop med fade." },
      { q: "Kan barberaren rekommendera vad som passar mitt ansikte?", a: "Ja, alltid. Berätta din livsstil och ansiktsform, så rekommenderar barberaren vad som fungerar bäst." }
    ],
    seoTitle: "Vad ska man säga till barberaren om en fade? | Guide",
    metaDescription: "Vet inte vad du ska säga till barberaren? Här är orden du behöver för att beskriva exakt vilken fade du vill ha – low, mid, high, skin eller taper.",
    publishedAt: "2025-09-15",
    readMinutes: 4
  },
  {
    slug: "hur-ofta-bor-man-klippa-sig-hos-barberare",
    title: "Hur ofta bör man klippa sig hos barberare?",
    category: "Herrfrisyrer",
    excerpt: "Hur ofta ska du klippa dig? Det beror på din frisyr och hårtyp. Konkreta riktlinjer för korta, medellånga och långa herrfrisyrer.",
    content: "En av de vanligaste frågorna vi får är: hur ofta ska man klippa sig? Svaret beror på frisyr, hårtyp och hur noga du är med att alltid se välvårdad ut.",
    relatedServices: ["herrklippning", "taper-fade"],
    relatedSalons: [],
    relatedGuides: ["vad-ska-man-saga-till-barberaren-om-man-vill-ha-en-fade", "herrfrisyrer-2026"],
    faq: [
      { q: "Hur ofta ska man klippa en fade?", a: "Var 3–4 veckor för att hålla formen. En skin fade kan behöva klippas redan efter 2–3 veckor." },
      { q: "Kan man klippa sig för ofta?", a: "Nej. Regelbundna besök håller frisyren i form och är ofta billigare totalt sett än att vänta för länge." },
      { q: "Vad händer om man väntar för länge?", a: "Frisyren förlorar sin form och det tar längre tid att återskapa den, vilket kan öka kostnaden." },
      { q: "Hur vet jag när det är dags?", a: "När nacken och sidorna börjar se oprecisa ut, eller när övergångarna i en fade inte längre syns tydligt." }
    ],
    seoTitle: "Hur ofta bör man klippa sig hos barberare? | Guide",
    metaDescription: "Hur ofta ska du klippa dig? Konkreta riktlinjer för fade, korta och längre herrfrisyrer – beroende på frisyr och hårtyp.",
    publishedAt: "2025-10-02",
    readMinutes: 3
  },
  {
    slug: "herrfrisyrer-2026",
    title: "Herrfrisyrer 2026 – de populäraste stilarna just nu",
    category: "Herrfrisyrer",
    excerpt: "De mest efterfrågade herrfrisyrerna 2026 – från textured crop till klassisk pompadour. Hitta din nästa stil.",
    content: "Herrfrisyrer 2026 handlar om textur, rörelse och precision. De populäraste stilarna blandar klassiska tekniker med moderna detaljer – och fungerar lika bra på kontoret som på helgen.",
    relatedServices: ["herrklippning", "taper-fade"],
    relatedSalons: [],
    relatedGuides: ["hur-ofta-bor-man-klippa-sig-hos-barberare", "vad-ska-man-saga-till-barberaren-om-man-vill-ha-en-fade"],
    faq: [
      { q: "Vad är den populäraste herrfrisyren 2026?", a: "Textured crop och mid taper fade är bland de mest efterfrågade stilarna på Tailor's Barber just nu." },
      { q: "Vilken frisyr passar ett runt ansikte?", a: "Frisyrer med volym på toppen och kortare sidor – som en high fade med längd uppe – balanserar ansiktsformen." },
      { q: "Är pompadouren fortfarande modern?", a: "Ja. En moderniserad pompadour med fade och textur är tidlös och efterfrågad." },
      { q: "Kan jag ta med en bild till barberaren?", a: "Absolut – det är det enklaste sättet att kommunicera exakt vilken stil du vill ha." }
    ],
    seoTitle: "Herrfrisyrer 2026 – populäraste stilarna för män | Guide",
    metaDescription: "De populäraste herrfrisyrerna 2026 – textured crop, taper fade, pompadour och mer. Hitta din nästa stil och boka hos Tailor's Barber.",
    publishedAt: "2025-11-10",
    readMinutes: 4
  },
  {
    slug: "ar-barberare-vart-priset-jamfort-med-vanlig-frisor",
    title: "Är barberare värt priset jämfört med vanlig frisör?",
    category: "Priser & råd",
    excerpt: "Barberare kostar ofta mer – men vad får du egentligen för pengarna? Vi reder ut skillnaderna.",
    content: "Priset för en herrklippning hos barberare är ofta 20–30% högre än på en vanlig frisörsalong. Men vad du får för pengarna är ofta väsentligt mer – i kompetens, teknik och upplevelse.",
    relatedServices: ["herrklippning", "taper-fade", "skaggtrimning"],
    relatedSalons: [],
    relatedGuides: ["barberare-eller-frisor-vad-ar-bast-for-man-i-stockholm", "vad-kostar-en-bra-barberare-i-stockholm-och-vad-ingar"],
    faq: [
      { q: "Varför kostar barberare mer?", a: "Spetskompetens på herrfrisyrer, mer tid per kund och specialutrustning för tekniker som fade och straight razor." },
      { q: "Vad kan barberare som frisörer inte kan?", a: "Skin fade, taper fade, skäggtrimning med rakkniv, line-ups och hot towel shave ingår typiskt i barberers kompetens." },
      { q: "Är det värt att betala mer?", a: "Om du söker en fade, skäggvård eller rena linjer – ja. Barberare levererar precisionstekniker som inte finns hos alla frisörer." },
      { q: "Vad kostar herrklippning hos Tailor's Barber?", a: "Herrklippning 450 kr, skäggtrimning 380 kr." }
    ],
    seoTitle: "Är barberare värt priset jämfört med frisör? | Guide",
    metaDescription: "Barberare vs frisör – vad är skillnaden och är det värt priset? Vi reder ut vad du får för pengarna hos en professionell barberare i Stockholm.",
    publishedAt: "2025-10-20",
    readMinutes: 4
  },
  {
    slug: "nar-ar-drop-in-hos-barberare-i-stockholm-ett-bra-val",
    title: "När är drop-in hos barberare i Stockholm ett bra val?",
    category: "Drop-in guider",
    excerpt: "Drop-in passar inte alltid. När är det ett bra alternativ och när bör du boka tid istället?",
    content: "Drop-in hos barberare är smidigt – men inte alltid den bästa lösningen. Beroende på tidpunkt, salong och vad du vill ha kan väntetiden variera kraftigt.",
    relatedServices: ["herrklippning", "taper-fade"],
    relatedSalons: ["ahlens-city", "taby", "akersberga", "arninge"],
    relatedGuides: ["hur-valjer-man-ratt-barberare"],
    faq: [
      { q: "Har Tailor's Barber drop-in?", a: "Ja, drop-in finns på alla fyra salonger i mån av plats." },
      { q: "Vilka dagar är bäst för drop-in?", a: "Vardagar tisdag–torsdag, gärna förmiddag eller tidig eftermiddag." },
      { q: "Kan jag garantera en plats med drop-in?", a: "Nej. För garanterad tid och specifik barberare bör du boka online." },
      { q: "Hur bokar jag tid online?", a: "Via BokaDirekt. Välj salong, barberare och tid – klart på 30 sekunder." }
    ],
    seoTitle: "Drop-in barberare Stockholm – när passar det? | Guide",
    metaDescription: "Är drop-in hos barberare ett bra val? Vi reder ut när drop-in fungerar och när du bör boka online istället.",
    publishedAt: "2025-09-28",
    readMinutes: 3
  },
  {
    slug: "vad-kostar-en-bra-barberare-i-stockholm-och-vad-ingar",
    title: "Vad kostar en bra barberare i Stockholm – och vad ingår?",
    category: "Priser & råd",
    excerpt: "Prisnivåerna för barberare i Stockholm – och vad som faktiskt ingår i priset hos en bra salong.",
    content: "Priserna för herrklippning i Stockholm varierar kraftigt – från under 300 kr till över 700 kr. Men pris ensamt säger lite om vad du faktiskt får.",
    relatedServices: ["herrklippning", "skaggtrimning", "taper-fade"],
    relatedSalons: [],
    relatedGuides: ["ar-barberare-vart-priset-jamfort-med-vanlig-frisor", "hur-valjer-man-ratt-barberare"],
    faq: [
      { q: "Vad kostar herrklippning i Stockholm?", a: "Typiskt 300–700 kr beroende på salong och servicenivå. Tailor's Barber tar 450 kr." },
      { q: "Vad ingår i en herrklippning?", a: "Konsultation, klippning, nacke och sidor, konturer och styling. Ibland schamponering." },
      { q: "Är det värt att betala mer?", a: "Ofta ja – mer betalt ger vanligtvis mer tid, bättre teknik och ett mer preciserat resultat." },
      { q: "Finns det dolda avgifter?", a: "Hos Tailor's Barber är priset fast. Ingen tilläggsavgift för styling eller konturer." }
    ],
    seoTitle: "Vad kostar en barberare i Stockholm – och vad ingår? | Guide",
    metaDescription: "Prisnivåer för barberare i Stockholm och vad som ingår i en herrklippning. Från budget till premium – så väljer du rätt.",
    publishedAt: "2025-11-05",
    readMinutes: 4
  },
  {
    slug: "barberare-eller-frisor-vad-ar-bast-for-man-i-stockholm",
    title: "Barberare eller frisör – vad är bäst för män i Stockholm?",
    category: "Priser & råd",
    excerpt: "Barberare eller frisör? Vi reder ut skillnaderna i kompetens, tjänster och upplevelse – och när du bör välja vad.",
    content: "Frågan om barberare eller frisör är vanlig. Svaret beror på vad du söker – och vad du vill ha ut av besöket.",
    relatedServices: ["herrklippning", "taper-fade", "skaggtrimning"],
    relatedSalons: [],
    relatedGuides: ["barber-frisor", "ar-barberare-vart-priset-jamfort-med-vanlig-frisor"],
    faq: [
      { q: "Vad är skillnaden mellan barberare och frisör?", a: "Barberare specialiserar sig på herr – fade, skägg och klassiska tekniker. Frisör klipper alla hårtyper och stilar, men har inte alltid djup kompetens på herrfrisyrer." },
      { q: "Är barberare dyrare?", a: "Ofta lite dyrare, men du får spetskompetens och mer tid per besök." },
      { q: "Kan frisörer göra en fade?", a: "Ibland – men en barberare har alltid bredare kompetens och erfarenhet av fade-tekniker." },
      { q: "Varför ska jag välja Tailor's Barber?", a: "Fyra salonger i Stockholm, 11 000+ betyg på BokaDirekt och barberare som specialiserat sig på herrfrisyrer sedan 2015." }
    ],
    seoTitle: "Barberare eller frisör – vad är bäst för män? | Guide",
    metaDescription: "Barberare vs frisör för män i Stockholm. Vi reder ut skillnaderna i kompetens, teknik, pris och upplevelse – och när du bör välja vad.",
    publishedAt: "2025-10-15",
    readMinutes: 4
  },
  {
    slug: "vad-kostar-en-bra-barberare-i-stockholm-och-vad-ingar-egentligen",
    title: "Vad kostar en bra barberare i Stockholm – och vad ingår egentligen?",
    category: "Priser & råd",
    excerpt: "Vad betalar du egentligen för när du går till en barberare? Vi bryter ned vad som ingår – och vad som gör skillnad i slutresultatet.",
    content: "En herrklippning kan kosta 280 kr eller 700 kr i Stockholm. Skillnaden syns inte alltid i vad som står på prislistan – men den syns i spegeln.",
    relatedServices: ["herrklippning", "skaggtrimning", "taper-fade"],
    relatedSalons: [],
    relatedGuides: ["vad-kostar-en-bra-barberare-i-stockholm-och-vad-ingar", "ar-barberare-vart-priset-jamfort-med-vanlig-frisor"],
    faq: [
      { q: "Vad ingår egentligen i 450 kr för herrklippning?", a: "Konsultation, klippning med sax och maskin, nacke och sidor, line-ups, styling och produkttips." },
      { q: "Finns det dolda avgifter?", a: "Nej. Priset hos Tailor's Barber är fast – inga tillägg för styling, konturer eller konsultation." },
      { q: "Varför skiljer priset mellan salonger?", a: "Kompetens, tid per kund, material och upplevelse. Lågt pris kan betyda kortare tid och lägre precision." },
      { q: "Är det billigare med bokning online?", a: "Samma pris oavsett hur du bokar – men bokning online garanterar din plats." }
    ],
    seoTitle: "Vad ingår egentligen hos en barberare i Stockholm? | Guide",
    metaDescription: "Vad betalar du för när du går till barberare? Vi bryter ned vad som faktiskt ingår i priset – och vad som gör skillnad i slutresultatet.",
    publishedAt: "2025-11-20",
    readMinutes: 3
  },
  {
    slug: "hur-valjer-man-ratt-barberare",
    title: "Hur väljer man rätt barberare?",
    category: "Priser & råd",
    excerpt: "Hur hittar du en barberare du kan lita på och återkomma till? Konkreta tips på vad du ska titta efter.",
    content: "En bra barberare kan förändra hur du ser på din egen stil. Att hitta rätt tar ibland lite tid – men med rätt strategi går det snabbare.",
    relatedServices: ["herrklippning", "taper-fade"],
    relatedSalons: ["ahlens-city", "taby", "akersberga", "arninge"],
    relatedGuides: ["basta-barberare-i-stockholm-2026", "vad-kostar-en-bra-barberare-i-stockholm-och-vad-ingar"],
    faq: [
      { q: "Hur hittar jag en bra barberare i Stockholm?", a: "Titta på recensioner på Google och BokaDirekt, fråga vänner och testa med en enklare klippning. Konsistenta höga betyg är ett bra tecken." },
      { q: "Ska jag alltid gå till samma barberare?", a: "Ja – en barberare som lär känna ditt hår levererar bättre resultat över tid." },
      { q: "Vad ska man tänka på vid första besöket?", a: "Ta med en bild på stilen du vill ha och berätta om din vardag och hur mycket tid du vill lägga på styling." },
      { q: "Hur vet jag om en barberare är bra på fade?", a: "Be om att se bilder på tidigare arbeten, titta på Instagram och läs recensioner – bra fades nämns specifikt i kommentarer." }
    ],
    seoTitle: "Hur väljer man rätt barberare? | Guide",
    metaDescription: "Så hittar du rätt barberare i Stockholm. Konkreta tips på vad du ska titta efter – recensioner, kommunikation och vad som gör en bra barberare.",
    publishedAt: "2025-10-08",
    readMinutes: 4
  },
  {
    slug: "frisyr-sjalvfortroende",
    title: "Frisyr och självförtroende – varför håret spelar roll",
    category: "Välmående",
    excerpt: "Forskning visar att en bra frisyr påverkar hur vi mår. Här är varför håret spelar roll – och hur du hittar en stil som stärker dig.",
    content: "Håret är ofta det första man lägger märke till hos en person – och det påverkar också hur vi uppfattar oss själva. En bra frisyr kan göra verklig skillnad för hur du mår varje dag.",
    relatedServices: ["herrklippning", "skaggtrimning"],
    relatedSalons: [],
    relatedGuides: ["hur-valjer-man-ratt-barberare", "herrfrisyrer-2026"],
    faq: [
      { q: "Kan en frisyr verkligen påverka självförtroendet?", a: "Ja – forskning och erfarenhet visar att en frisyr man trivs med positivt påverkar hur man mår och uppfattas." },
      { q: "Hur hittar jag en stil som passar mig?", a: "Prata med din barberare om din ansiktsform, hårtyp och livsstil. Ta med bilder på stilar du gillar som utgångspunkt." },
      { q: "Hur ofta bör jag klippa mig för att alltid se välvårdad ut?", a: "Var 3–6 veckor beroende på frisyr. Korta och fades kräver tätare besök." }
    ],
    seoTitle: "Frisyr och självförtroende – varför håret spelar roll | Guide",
    metaDescription: "En bra frisyr påverkar mer än du tror. Läs om sambandet mellan hår och självkänsla – och hur du hittar en stil som passar dig.",
    publishedAt: "2025-09-05",
    readMinutes: 3
  },
  {
    slug: "barber-frisor",
    title: "Barber vs frisör – vad är skillnaden?",
    category: "Priser & råd",
    excerpt: "Vad skiljer egentligen en barberare från en vanlig frisör? Vi reder ut teknik, utbildning, tjänster och atmosfär.",
    content: "Många använder begreppen barberare och frisör synonymt – men det finns tydliga skillnader i vad de gör, hur de är utbildade och vad de är bra på.",
    relatedServices: ["herrklippning", "taper-fade", "skaggtrimning"],
    relatedSalons: [],
    relatedGuides: ["barberare-eller-frisor-vad-ar-bast-for-man-i-stockholm", "ar-barberare-vart-priset-jamfort-med-vanlig-frisor"],
    faq: [
      { q: "Vad är skillnaden mellan barberare och frisör?", a: "Barberare specialiserar sig på herrfrisyrer och skägg. Frisörer har bredare utbildning men inte alltid djup kompetens på fade och barbertekniker." },
      { q: "Vad är en barbershop?", a: "En salong som specialiserar sig på herrfrisyrer – typiskt med fokus på fade, skägg, klassisk rakning och precision." },
      { q: "Kan en frisör göra hot towel shave?", a: "Sällan. Hot towel shave och straight razor är tekniker som hör till barbershopen." },
      { q: "Var är närmaste barbershop i Stockholm?", a: "Tailor's Barber finns på Täby Centrum, Åhléns City, Arninge Centrum och i Åkersberga." }
    ],
    seoTitle: "Barber vs frisör – vad är skillnaden? | Guide",
    metaDescription: "Vad skiljer en barberare från en frisör? Vi reder ut teknik, utbildning, tjänster och atmosfär – och hjälper dig välja rätt.",
    publishedAt: "2025-09-20",
    readMinutes: 3
  },
  {
    slug: "basta-barberare-i-stockholm-2026",
    title: "Bästa barberare i Stockholm 2026",
    category: "Lokala guider",
    excerpt: "Vad gör en barberare till den bästa i Stockholm? Vi tittar på vad som verkligen spelar roll – och varför Tailor's Barber hamnar i topp.",
    content: "Stockholm har hundratals barbersalonger. Att hitta en som konsekvent levererar hög kvalitet, har välutbildade barberare och ett brett utbud av tjänster är inte alltid enkelt.",
    relatedServices: ["herrklippning", "taper-fade", "skaggtrimning"],
    relatedSalons: ["ahlens-city", "taby", "akersberga", "arninge"],
    relatedGuides: ["hur-valjer-man-ratt-barberare", "barberare-eller-frisor-vad-ar-bast-for-man-i-stockholm"],
    faq: [
      { q: "Vilken är bästa barberaren i Stockholm?", a: "Det beror på vad du söker, men Tailor's Barber rankas konsekvent högt med 11 000+ BokaDirekt-betyg och fyra salonger i Stockholm." },
      { q: "Hur väljer jag rätt salong?", a: "Välj baserat på plats, recensioner och om de specialiserar sig på herrfrisyrer och de tjänster du söker." },
      { q: "Kan jag boka online?", a: "Ja, alla tider på Tailor's Barber bokas enkelt via BokaDirekt." },
      { q: "Var ligger Tailor's Barbers salonger?", a: "Åhléns City (Stockholm city), Täby Centrum, Arninge Centrum och Åkersberga." }
    ],
    seoTitle: "Bästa barberare i Stockholm 2026 | Guide",
    metaDescription: "Vad gör en barberare till den bästa i Stockholm? Vi tittar på vad som spelar roll och presenterar Tailor's Barber – en av Stockholms mest välrecenserade barbershopkedjor.",
    publishedAt: "2026-01-05",
    readMinutes: 4
  },
  {
    slug: "skaggstilar-2026",
    title: "Skäggstilar 2026 – de populäraste stilarna för män",
    category: "Skäggguider",
    excerpt: "Vilka skäggstilar dominerar 2026? Från välvårdad stubb till längre skägg – de stilar som syns mest just nu.",
    content: "Skägg är fortfarande en stark trend 2026 – men det välvårdade, precisa skägget vinner mark mot det ovårdade. Här är stilarna som dominerar just nu.",
    relatedServices: ["skaggtrimning", "har-skagg"],
    relatedSalons: [],
    relatedGuides: ["skaggbalsam-valvardat-skagg", "barbershop-skaggtrimning-stockholm"],
    faq: [
      { q: "Vilken skäggstil är populärast 2026?", a: "Kort välvårdat skägg (3–7 mm) med tydliga linjer. Välvårdad stubb är också stark." },
      { q: "Hur håller jag skägget välvårdat hemma?", a: "Trimma regelbundet, använd skäggolja, och besök barberaren för att behålla konturerna skarpa." },
      { q: "Hur ofta bör jag gå till barberaren för skägget?", a: "Var 3–5 veckor för korta skägg. Längre skägg kan gå lite längre men behöver regelbunden formning." },
      { q: "Passar skägg alla ansiktsformer?", a: "Olika skäggstilar passar olika ansiktsformer. En barberare kan rådgöra om vad som fungerar bäst för just dig." }
    ],
    seoTitle: "Skäggstilar 2026 – populäraste stilarna för män | Guide",
    metaDescription: "Vilka skäggstilar dominerar 2026? Stubb, kort skägg och välformat längre skägg – vi går igenom de populäraste stilarna och hur du underhåller dem.",
    publishedAt: "2026-01-20",
    readMinutes: 4
  },
  {
    slug: "skaggbalsam-valvardat-skagg",
    title: "Skäggolja och skäggbalsam – hur du håller skägget välvårdat",
    category: "Skäggguider",
    excerpt: "Skillnaden mellan skäggolja och skäggbalsam, hur du applicerar dem och hur ofta du bör använda dem.",
    content: "Skäggvård hemma är avgörande för ett välvårdat skägg. Skäggolja och skäggbalsam fyller olika funktioner – och tillsammans ger de det bästa resultatet.",
    relatedServices: ["skaggtrimning", "har-skagg"],
    relatedSalons: [],
    relatedGuides: ["skaggstilar-2026", "barbershop-skaggtrimning-stockholm"],
    faq: [
      { q: "Behöver man använda skäggolja?", a: "Inte nödvändigt, men det gör stor skillnad för hud och skägg – minskar klåda, torrhet och ger ett mjukare skägg." },
      { q: "Vad är bäst för ett kort skägg?", a: "Skäggolja räcker för korta skägg. Skäggbalsam behövs mer för längre skägg som behöver form." },
      { q: "Hur väljer jag rätt skäggprodukt?", a: "Titta på skäggets längd och ditt behov av hållning. Fråga din barberare – de kan rekommendera produkter baserade på ditt skägg." },
      { q: "Säljer Tailor's Barber skäggprodukter?", a: "Ja, vi har ett urval av skäggolja, skäggbalsam och övriga grooming-produkter i våra salonger." }
    ],
    seoTitle: "Skäggolja och skäggbalsam – guide för välvårdat skägg | Guide",
    metaDescription: "Skillnaden mellan skäggolja och skäggbalsam – hur du använder dem och hur du håller skägget välvårdat hemma och hos barberaren.",
    publishedAt: "2025-12-01",
    readMinutes: 4
  },
  {
    slug: "barbershop-skaggtrimning-stockholm",
    title: "Skäggtrimning på barbershop i Stockholm – vad ingår?",
    category: "Skäggguider",
    excerpt: "Vad ingår i en professionell skäggtrimning hos barberare? Och varför är det bättre än att trimma hemma?",
    content: "En professionell skäggtrimning hos barberare är mer än en snabb trim. Det är formning, linjering och skäggvård utförd av en specialist med rätt verktyg.",
    relatedServices: ["skaggtrimning", "har-skagg", "herrklippning"],
    relatedSalons: ["ahlens-city", "taby", "akersberga", "arninge"],
    relatedGuides: ["skaggstilar-2026", "skaggbalsam-valvardat-skagg"],
    faq: [
      { q: "Vad ingår i en skäggtrimning hos Tailor's Barber?", a: "Rådgivning, formning, linjering av hals, kinder och mustasch, och styling med skäggprodukt. 380 kr, ca 20–30 min." },
      { q: "Hur lång tid tar en skäggtrimning?", a: "Cirka 20–30 minuter." },
      { q: "Kan jag kombinera skäggtrimning med herrklippning?", a: "Ja – klippning + skägg är ett populärt val. Boka båda i samma session." },
      { q: "Hur ofta bör man gå till barberaren för skägget?", a: "Var 3–4 veckor för ett välvårdat resultat." }
    ],
    seoTitle: "Skäggtrimning barbershop Stockholm – vad ingår? | Guide",
    metaDescription: "Vad ingår i en professionell skäggtrimning hos barberare i Stockholm? Pris, tid och varför det är bättre än hemmatrimning.",
    publishedAt: "2025-12-15",
    readMinutes: 3
  },
  {
    slug: "misstag-vaxer-ut-haret",
    title: "Misstag när du låter håret växa ut – och hur du undviker dem",
    category: "Herrfrisyrer",
    excerpt: "Att låta håret växa ut kan se konstigt ut i mellanstadiet. Här är de vanligaste misstagen – och hur du undviker dem.",
    content: "Att låta håret växa ut kräver tålamod – och rätt strategi. Utan det hamnar många i en fas som varken ser klippt eller avsiktlig ut.",
    relatedServices: ["herrklippning"],
    relatedSalons: [],
    relatedGuides: ["herrfrisyrer-2026", "hur-ofta-bor-man-klippa-sig-hos-barberare"],
    faq: [
      { q: "Ska jag sluta klippa mig när jag växer ut håret?", a: "Nej – fortsätt boka barberaren var 5–6 vecka för formning utan längdjustering. Det håller utväxten snygg." },
      { q: "Hur lång tid tar det att växa ut en fade?", a: "Det beror på utgångslängden. En fade till medellångt hår tar typiskt 3–6 månader." },
      { q: "Vilka produkter passar under utväxten?", a: "Lätt vax, salt spray eller texturiserande kräm. Fråga din barberare för rekommendationer baserade på din hårtyp." },
      { q: "Kan barberaren hjälpa mig planera utväxten?", a: "Ja, absolut. En konsultation om mål och plan är alltid välkommen hos oss." }
    ],
    seoTitle: "Misstag när du låter håret växa ut – och hur du undviker dem | Guide",
    metaDescription: "Vanliga misstag när du växer ut håret – och hur du undviker den jobbiga mellanfasen. Tips om form, produkter och tålamod.",
    publishedAt: "2026-02-01",
    readMinutes: 4
  },
];

export const guideBySlug = Object.fromEntries(guides.map((g) => [g.slug, g])) as Record<string, Guide>;

export const guidesByCategory = guides.reduce((acc, g) => {
  if (!acc[g.category]) acc[g.category] = [];
  acc[g.category].push(g);
  return acc;
}, {} as Record<string, Guide[]>);
